import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getStorage, saveBookmarks, saveTags, saveSettings } from '@/utils/storage';
import { Bookmark, Tag } from '@/types';
import TagManager from '@/components/TagManager';
import BookmarkCard from '@/components/BookmarkCard';
import BookmarkEditModal from '@/components/BookmarkEditModal';
import BookmarkletInfo from '@/components/BookmarkletInfo';
import AIPlatforms from '@/components/AIPlatforms';
import { ArrowLeft, Download, Upload, Plus, Bot, BookMarked, CheckSquare, Trash2, Tag as TagIcon, X, Maximize2, Grid, List } from 'lucide-react';
import { exportData, importData } from '@/utils/dataTransfer';
import { v4 as uuidv4 } from 'uuid';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useInView } from 'react-intersection-observer';

// Helper component for batch tag selection
const BatchTagSelector = ({ tags, onConfirm, onCancel }: { tags: Tag[], onConfirm: (ids: string[]) => void, onCancel: () => void }) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const toggleTag = (id: string) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedIds(newSet);
    };

    return (
        <>
             <div className="flex flex-wrap gap-2 mb-6 max-h-60 overflow-y-auto">
                {tags.map(tag => (
                    <button
                        key={tag.id}
                        onClick={() => toggleTag(tag.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                            selectedIds.has(tag.id)
                            ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/50 dark:text-blue-200' 
                            : 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                        }`}
                    >
                        {tag.name}
                    </button>
                ))}
            </div>
            <div className="flex justify-end gap-2">
                <button 
                    onClick={onCancel}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 rounded-lg"
                >
                    取消
                </button>
                <button 
                    onClick={() => onConfirm(Array.from(selectedIds))}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    确认添加
                </button>
            </div>
        </>
    );
}

const SortableBookmarkItem = ({ bookmark, allTags, onDelete, onEdit, onTogglePin, isSelected, isSelectionMode, onToggleSelect, viewMode }: { bookmark: Bookmark, allTags: Tag[], onDelete: (id: string) => void, onEdit: (b: Bookmark) => void, onTogglePin: (id: string) => void, isSelected: boolean, isSelectionMode: boolean, onToggleSelect: (id: string) => void, viewMode: 'grid' | 'list' }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: bookmark.id, disabled: isSelectionMode }); // Disable drag in selection mode

    const { ref: inViewRef, inView } = useInView({
        triggerOnce: true,
        rootMargin: '200px 0px', // Preload when within 200px
    });

    // Merge refs
    const setRefs = (element: HTMLDivElement) => {
        setNodeRef(element);
        inViewRef(element);
    };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
    };

    return (
        <div ref={setRefs} style={style} {...attributes} {...listeners} className="h-full">
            {inView ? (
                <BookmarkCard
                    bookmark={bookmark}
                    allTags={allTags}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onTogglePin={onTogglePin}
                    isSelected={isSelected}
                    isSelectionMode={isSelectionMode}
                    onToggleSelect={onToggleSelect}
                    viewMode={viewMode}
                />
            ) : (
                <div className={`bg-slate-50 dark:bg-slate-800 rounded-xl animate-pulse ${viewMode === 'list' ? 'h-20' : 'h-full min-h-[200px]'}`}></div>
            )}
        </div>
    );
};

const Bookmarks = () => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'bookmarks' | 'ai'>('bookmarks');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid'); // View mode state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);
  const [selectedBookmarkIds, setSelectedBookmarkIds] = useState<Set<string>>(new Set());
  const [isBatchTagModalOpen, setIsBatchTagModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const [searchParams, setSearchParams] = useSearchParams();

  // Side Panel Detection
  const [isSidePanel, setIsSidePanel] = useState(window.innerWidth < 500);
  useEffect(() => {
    const handleResize = () => setIsSidePanel(window.innerWidth < 500);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate bookmark counts per tag
  const bookmarksCount = React.useMemo(() => {
      const counts: Record<string, number> = {};
      counts['all'] = bookmarks.length;
      
      bookmarks.forEach(bookmark => {
          bookmark.tags.forEach(tagId => {
              counts[tagId] = (counts[tagId] || 0) + 1;
          });
      });
      return counts;
  }, [bookmarks]);

  const filteredBookmarks = React.useMemo(() => {
    if (selectedTagId) {
        return bookmarks.filter(b => b.tags.includes(selectedTagId));
    }
    
    // All bookmarks view: Tagged items first, Untagged items last
    const taggedBookmarks = bookmarks.filter(b => b.tags && b.tags.length > 0);
    const untaggedBookmarks = bookmarks.filter(b => !b.tags || b.tags.length === 0);
    return [...taggedBookmarks, ...untaggedBookmarks];
  }, [bookmarks, selectedTagId]);

  useEffect(() => {
    const initData = async () => {
        const data = await getStorage();
        let currentTags = data.tags;
        let currentBookmarks = data.bookmarks;
        let settings = data.settings;
        let hasChanges = false;

        // 1. Find or Create "Recommended Home" Tag
        let recommendedTag = currentTags.find(t => t.name === '推荐首页');
        if (!recommendedTag) {
            recommendedTag = {
                id: uuidv4(),
                name: '推荐首页',
                color: '#3b82f6' // Blue
            };
            currentTags = [...currentTags, recommendedTag];
            hasChanges = true;
        }

        // 2. Migrate existing Quick Dial items if any
        if (settings.quickDialItems && settings.quickDialItems.length > 0) {
            const newBookmarks = settings.quickDialItems.map(item => ({
                id: uuidv4(), // Generate new ID to avoid conflict
                title: item.title,
                url: item.url,
                favicon: item.favicon,
                thumbnail: item.thumbnail,
                tags: [recommendedTag!.id], // Add to recommended tag
                createdAt: Date.now(),
                visitCount: item.visitCount || 0
            }));
            currentBookmarks = [...currentBookmarks, ...newBookmarks];
            settings.quickDialItems = []; // Clear old items
            hasChanges = true;
        }

        // 3. Set default source if not set
        if (!settings.quickDialSourceTagId) {
            settings.quickDialSourceTagId = recommendedTag!.id;
            hasChanges = true;
        }

        if (hasChanges) {
            await Promise.all([
                saveTags(currentTags),
                saveBookmarks(currentBookmarks),
                saveSettings(settings)
            ]);
        }

        setTags(currentTags.sort((a, b) => {
            if (a.name === '推荐首页') return -1;
            if (b.name === '推荐首页') return 1;
            return 0;
        }));
        // Sort bookmarks: Pinned first, then by createdAt desc
        setBookmarks(currentBookmarks.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return 0; // Keep existing order for non-pinned
        }));
        setIsLoading(false);
    };

    initData();
    
    // Check for "add" query param from bookmarklet
    const addMode = searchParams.get('add');
    const url = searchParams.get('url');
    const title = searchParams.get('title');

    if (addMode === 'true' && url) {
        setEditingBookmark({
            id: uuidv4(),
            title: title || 'New Bookmark',
            url: url,
            favicon: `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}`,
            thumbnail: '',
            tags: [],
            createdAt: Date.now(),
            visitCount: 0
        });
        setIsEditModalOpen(true);
        // Clean URL
        setSearchParams({});
    }

    // Listen for background messages (e.g. from right-click add)
    const handleMessage = (message: any) => {
        if (message.type === 'DATA_UPDATED') {
            loadData();
        }
    };
    
    // chrome.runtime.onMessage is not available in standard web context, only in extension pages.
    // But since this page might be running as an extension page (chrome-extension://...), we can check.
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.onMessage) {
        chrome.runtime.onMessage.addListener(handleMessage);
    }
    
    // Also listen for storage events for multi-tab sync (Local Mode)
    const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'webtags_data') {
            loadData();
        }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.onMessage) {
            chrome.runtime.onMessage.removeListener(handleMessage);
        }
        window.removeEventListener('storage', handleStorageChange);
    };

  }, [searchParams]); // Keep searchParams in deps

  const loadData = async () => {
    setIsLoading(true);
    const data = await getStorage();
    const sortedBookmarks = data.bookmarks.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
    });
    setBookmarks(sortedBookmarks);
    setTags(data.tags.sort((a, b) => {
        if (a.name === '推荐首页') return -1;
        if (b.name === '推荐首页') return 1;
        return 0;
    }));
    setIsLoading(false);
  };

  const handleTogglePin = async (id: string) => {
      const updatedBookmarks = bookmarks.map(b => {
          if (b.id === id) {
              return { ...b, isPinned: !b.isPinned };
          }
          return b;
      }).sort((a, b) => {
          // 1. Pinned first
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          
          // 2. If pin status is same, fallback to createdAt (newest first)
          // or keep original relative order if we had a stable sort.
          // Since we want to "restore" position when unpinned, sorting by createdAt 
          // is a good default for "unpinned" items to return to their chronological spot.
          return (b.createdAt || 0) - (a.createdAt || 0);
      });
      
      setBookmarks(updatedBookmarks);
      await saveBookmarks(updatedBookmarks);
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await importData(file);
      loadData(); // Reload data after import
    }
    // Reset input
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }
  };

  const handleAddTag = async (newTag: Tag) => {
    const updatedTags = [...tags, newTag].sort((a, b) => {
        if (a.name === '推荐首页') return -1;
        if (b.name === '推荐首页') return 1;
        return 0;
    });
    setTags(updatedTags);
    await saveTags(updatedTags);
  };
  
  const handleTagReorder = async (newTags: Tag[]) => {
      setTags(newTags);
      await saveTags(newTags);
  };
  
  const handleDeleteTag = async (tagId: string) => {
      const tagToDelete = tags.find(t => t.id === tagId);
      if (tagToDelete?.name === '推荐首页') {
          alert('“推荐首页”为系统预设标签，不可删除');
          return;
      }
      const updatedTags = tags.filter(t => t.id !== tagId);
      setTags(updatedTags);
      await saveTags(updatedTags);
  };

  const handleCleanEmptyTags = async () => {
      if (!confirm('确定要删除所有未使用的标签吗？（“推荐首页”标签除外）')) return;
      
      const usedTagIds = new Set(bookmarks.flatMap(b => b.tags));
      const newTags = tags.filter(t => t.name === '推荐首页' || usedTagIds.has(t.id));
      
      const deletedCount = tags.length - newTags.length;
      if (deletedCount > 0) {
          setTags(newTags);
          await saveTags(newTags);
          alert(`已清理 ${deletedCount} 个无效标签`);
      } else {
          alert('没有发现无效标签');
      }
  };

  const handleDeleteBookmark = async (id: string) => {
    const updatedBookmarks = bookmarks.filter(b => b.id !== id);
    setBookmarks(updatedBookmarks);
    await saveBookmarks(updatedBookmarks);
    
    // Also remove from selection if exists
    if (selectedBookmarkIds.has(id)) {
        const newSelection = new Set(selectedBookmarkIds);
        newSelection.delete(id);
        setSelectedBookmarkIds(newSelection);
    }
  };

  const handleSaveBookmark = async (bookmark: Bookmark) => {
      let updatedBookmarks;
      if (bookmarks.some(b => b.id === bookmark.id)) {
          updatedBookmarks = bookmarks.map(b => b.id === bookmark.id ? bookmark : b);
      } else {
          updatedBookmarks = [...bookmarks, bookmark];
      }
      
      // Sort pinned items first
      updatedBookmarks.sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return (b.createdAt || 0) - (a.createdAt || 0);
      });
      
      setBookmarks(updatedBookmarks);
      await saveBookmarks(updatedBookmarks);
      setIsEditModalOpen(false);
  };

  const handleBookmarkDragEnd = async (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
          const oldIndex = bookmarks.findIndex((b) => b.id === active.id);
          const newIndex = bookmarks.findIndex((b) => b.id === over.id);
          
          if (oldIndex !== -1 && newIndex !== -1) {
              const newBookmarks = arrayMove(bookmarks, oldIndex, newIndex);
              setBookmarks(newBookmarks);
              await saveBookmarks(newBookmarks);
          }
      }
  };


  
  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        const isInputFocused = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;

        if (e.key === 'Delete' && !isInputFocused && selectedBookmarkIds.size > 0 && activeTab === 'bookmarks') {
            handleBatchDelete();
        }
        
        if (e.key === 'Escape' && selectedBookmarkIds.size > 0) {
            setSelectedBookmarkIds(new Set());
        }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBookmarkIds, activeTab, bookmarks]);

  // Box Selection Logic
  const [isSelecting, setIsSelecting] = useState(false);
  const selectionStartRef = useRef<{x: number, y: number} | null>(null);
  const selectionBoxRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
      if (activeTab !== 'bookmarks') return;

      const handleMouseDown = (e: MouseEvent) => {
          // Ignore clicks on buttons/inputs or if already dragging
          // Also ignore if we are interacting with Sortable items (dragging handle)
          // Sortable items usually have touch-action: none or similar, or we can check if we are clicking a drag handle.
          // Here we check for .sortable-item and .batch-controls.
          // Additionally, let's check if the click target is part of a drag operation from dnd-kit.
          // However, dnd-kit handles its own events. The issue is that mousedown on the item might trigger this selection start.
          
          if ((e.target as HTMLElement).closest('button, a, input, .batch-controls')) return;
          
          // If we are clicking on a draggable item, we should probably NOT start selection immediately,
          // UNLESS it's a click-and-drag on empty space.
          // But dnd-kit usually handles drag on the item itself.
          // If the user drags ON a card, it should reorder. If they drag ON BACKGROUND, it should select.
          // So we check if we are clicking on a bookmark card.
          if ((e.target as HTMLElement).closest('.bookmark-card-container')) return;

          setIsSelecting(true);
          selectionStartRef.current = { x: e.pageX, y: e.pageY }; // Use pageX/Y for absolute document coordinates
          
          if (selectionBoxRef.current) {
              selectionBoxRef.current.style.left = `${e.pageX}px`;
              selectionBoxRef.current.style.top = `${e.pageY}px`;
              selectionBoxRef.current.style.width = '0px';
              selectionBoxRef.current.style.height = '0px';
              selectionBoxRef.current.style.display = 'block';
          }
      };

      const handleMouseMove = (e: MouseEvent) => {
          if (!isSelecting || !selectionStartRef.current || !selectionBoxRef.current) return;

          const currentX = e.pageX;
          const currentY = e.pageY;
          const startX = selectionStartRef.current.x;
          const startY = selectionStartRef.current.y;

          const left = Math.min(currentX, startX);
          const top = Math.min(currentY, startY);
          const width = Math.abs(currentX - startX);
          const height = Math.abs(currentY - startY);

          selectionBoxRef.current.style.left = `${left}px`;
          selectionBoxRef.current.style.top = `${top}px`;
          selectionBoxRef.current.style.width = `${width}px`;
          selectionBoxRef.current.style.height = `${height}px`;
      };

      const handleMouseUp = (e: MouseEvent) => {
          if (!isSelecting || !selectionStartRef.current || !selectionBoxRef.current) return;

          // Calculate selection
          const boxRect = selectionBoxRef.current.getBoundingClientRect();
          // Adjust boxRect to page coordinates for comparison if items are scrolled?
          // No, getBoundingClientRect returns viewport coordinates.
          // We should compare viewport coordinates of box vs items.
          
          const newSelection = new Set<string>(e.shiftKey ? selectedBookmarkIds : []); // Shift to append

          // If box is tiny, ignore (prevent accidental clears on simple clicks)
          if (boxRect.width > 5 && boxRect.height > 5) {
               document.querySelectorAll('.bookmark-card-container').forEach((el) => {
                  const itemRect = el.getBoundingClientRect();
                  
                  // Intersection Check
                  if (
                      boxRect.left < itemRect.right &&
                      boxRect.right > itemRect.left &&
                      boxRect.top < itemRect.bottom &&
                      boxRect.bottom > itemRect.top
                  ) {
                      const id = el.getAttribute('data-id');
                      if (id) newSelection.add(id);
                  }
              });
              setSelectedBookmarkIds(newSelection);
          }

          setIsSelecting(false);
          selectionStartRef.current = null;
          selectionBoxRef.current.style.display = 'none';
      };

      // Bind to document to catch moves outside container
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
          document.removeEventListener('mousedown', handleMouseDown);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
      };
  }, [isSelecting, activeTab, filteredBookmarks, selectedBookmarkIds]);


  const handleUpdateAllTagsColor = async (color: string) => {
    if (!confirm('确定要将所有标签统一修改为该颜色吗？')) return;
    
    const updatedTags = tags.map(t => t.name === '推荐首页' ? t : { ...t, color });
    setTags(updatedTags);
    await saveTags(updatedTags);
  };

  const handleEditTag = async (tag: Tag) => {
      const updatedTags = tags.map(t => t.id === tag.id ? tag : t);
      setTags(updatedTags);
      await saveTags(updatedTags);
  };

  const handleBatchDelete = async () => {
      if (!confirm(`确定要删除选中的 ${selectedBookmarkIds.size} 个书签吗？`)) return;
      
      const updatedBookmarks = bookmarks.filter(b => !selectedBookmarkIds.has(b.id));
      setBookmarks(updatedBookmarks);
      await saveBookmarks(updatedBookmarks);
      setSelectedBookmarkIds(new Set());
  };

  const handleBatchAddTags = async (tagIds: string[]) => {
      if (tagIds.length === 0) return;
      
      const updatedBookmarks = bookmarks.map(b => {
          if (selectedBookmarkIds.has(b.id)) {
              // Add new tags, avoid duplicates
              const newTags = [...new Set([...b.tags, ...tagIds])];
              return { ...b, tags: newTags };
          }
          return b;
      });
      setBookmarks(updatedBookmarks);
      await saveBookmarks(updatedBookmarks);
      setIsBatchTagModalOpen(false);
      setSelectedBookmarkIds(new Set<string>());
      alert('批量添加标签成功');
  };

  // Selection Logic
  const lastSelectedIdRef = useRef<string | null>(null);

  // Select Group Logic
  const handleSelectGroup = () => {
      if (!selectedTagId) {
          // If "All" is selected (selectedTagId is null), select ALL bookmarks
          const allIds = bookmarks.map(b => b.id);
          setSelectedBookmarkIds(new Set(allIds));
      } else {
          // Select only bookmarks in the current tag group
          const groupIds = bookmarks
              .filter(b => b.tags.includes(selectedTagId))
              .map(b => b.id);
          setSelectedBookmarkIds(new Set(groupIds));
      }
  };

  const handleToggleSelect = (id: string, event?: React.MouseEvent) => {
      if (isSidePanel) return; // Disable selection in side panel

      const newSelection = new Set(selectedBookmarkIds);
      
      // Shift + Click for range selection
      if (event?.shiftKey && lastSelectedIdRef.current) {
          const lastIndex = filteredBookmarks.findIndex(b => b.id === lastSelectedIdRef.current);
          const currentIndex = filteredBookmarks.findIndex(b => b.id === id);
          
          if (lastIndex !== -1 && currentIndex !== -1) {
              const start = Math.min(lastIndex, currentIndex);
              const end = Math.max(lastIndex, currentIndex);
              
              for (let i = start; i <= end; i++) {
                  newSelection.add(filteredBookmarks[i].id);
              }
          }
      } else {
          // Normal Toggle
          if (newSelection.has(id)) {
              newSelection.delete(id);
          } else {
              newSelection.add(id);
          }
      }
      
      lastSelectedIdRef.current = id;
      setSelectedBookmarkIds(newSelection);
  };

  return (
    <div className={`min-h-screen bg-slate-50 dark:bg-slate-900 ${isSidePanel ? 'p-4' : 'p-8'}`}>
      <div className="max-w-7xl mx-auto">
        {!isSidePanel && (
            <div className="flex flex-col mb-8 gap-4">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                            <ArrowLeft size={24} className="text-slate-600 dark:text-slate-300" />
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">收藏夹管理</h1>
                    </div>
                </div>
                
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center bg-white dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
                        <button 
                            onClick={() => setActiveTab('bookmarks')}
                            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === 'bookmarks' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}
                        >
                            <BookMarked size={16} /> 全部收藏
                        </button>
                        <button 
                            onClick={() => setActiveTab('ai')}
                            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === 'ai' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'}`}
                        >
                            <Bot size={16} /> AI 助手
                        </button>
                    </div>

                    <div className="flex gap-2">
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleFileChange} 
                            className="hidden" 
                            accept=".json,.html,.htm" 
                        />
                        <button 
                            onClick={handleImport}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750"
                            title="支持导入浏览器导出的 HTML 书签文件"
                        >
                            <Upload size={18} /> 一键导入
                        </button>
                        <button 
                            onClick={exportData}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750"
                        >
                            <Download size={18} /> 导出
                        </button>
                        {/* View Mode Toggle */}
                        <div className="flex bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-slate-100 dark:bg-slate-700 text-blue-600 dark:text-blue-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                title="网格视图"
                            >
                                <Grid size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-slate-100 dark:bg-slate-700 text-blue-600 dark:text-blue-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                title="列表视图"
                            >
                                <List size={18} />
                            </button>
                        </div>

                        <button 
                            onClick={() => { setEditingBookmark(null); setIsEditModalOpen(true); }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
                        >
                            <Plus size={18} /> 添加
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Side Panel Header - Minimal */}
        {isSidePanel && (
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-slate-50 dark:bg-slate-900 z-10 py-2">
                <h1 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <BookMarked size={18} className="text-blue-600" /> 我的收藏
                </h1>
            </div>
        )}

        {isLoading ? (
            <div className={`grid gap-4 mt-4 ${isSidePanel ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'}`}>
                {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="bg-white dark:bg-slate-800 rounded-xl p-4 h-[180px] animate-pulse flex flex-col gap-4 border border-slate-100 dark:border-slate-700">
                        <div className="w-full aspect-video bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                    </div>
                ))}
            </div>
        ) : (
            <>
                {activeTab === 'bookmarks' && (
                    <>
                        <div className="flex justify-between items-center mb-4">
                             {/* Tag Manager - Hide in Side Panel or simplify? User said "Only show bookmark list". 
                                 Maybe keep tags for filtering but make it compact? 
                                 For now, let's keep it but it might scroll. */}
                             {!isSidePanel && (
                                <TagManager
                                    tags={tags}
                                    selectedTagId={selectedTagId}
                                    onSelectTag={setSelectedTagId}
                                    onAddTag={handleAddTag}
                                    onDeleteTag={handleDeleteTag}
                                    onReorderTags={handleTagReorder}
                                    onCleanTags={handleCleanEmptyTags}
                                    bookmarksCount={bookmarksCount}
                                    onEditTag={handleEditTag}
                                    onUpdateAllTagsColor={handleUpdateAllTagsColor}
                                />
                             )}
                             {isSidePanel && (
                                 <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
                                     <div className="flex gap-2">
                                         <button
                                             onClick={() => setSelectedTagId(null)}
                                             className={`px-3 py-1 rounded-full text-xs whitespace-nowrap border ${!selectedTagId ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'}`}
                                         >
                                             全部
                                         </button>
                                         {tags.map(tag => (
                                             <button
                                                 key={tag.id}
                                                 onClick={() => setSelectedTagId(tag.id)}
                                                 className={`px-3 py-1 rounded-full text-xs whitespace-nowrap border flex items-center gap-1 ${selectedTagId === tag.id ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/50 dark:text-blue-200' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'}`}
                                             >
                                                 <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }}></span>
                                                 {tag.name}
                                             </button>
                                         ))}
                                     </div>
                                 </div>
                             )}
                        </div>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBookmarkDragEnd}>
                    <div ref={containerRef} className="relative min-h-[500px]">
                        {/* Selection Box - Disable in side panel for simplicity */}
                        {!isSidePanel && (
                            <div 
                                ref={selectionBoxRef} 
                                className="fixed border-2 border-blue-500 bg-blue-500/20 z-50 pointer-events-none" 
                                style={{ display: 'none' }}
                            ></div>
                        )}

                        <SortableContext items={filteredBookmarks.map(b => b.id)} strategy={rectSortingStrategy}>
                            <div className={`grid gap-4 ${viewMode === 'list' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2' : (isSidePanel ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5')}`}>
                                {filteredBookmarks.map(bookmark => (
                                    <div key={bookmark.id} className="bookmark-card-container h-full" data-id={bookmark.id}>
                                        <SortableBookmarkItem
                                            bookmark={bookmark}
                                            allTags={tags}
                                            onDelete={handleDeleteBookmark}
                                            onEdit={(b) => { setEditingBookmark(b); setIsEditModalOpen(true); }}
                                            onTogglePin={handleTogglePin}
                                            isSelected={selectedBookmarkIds.has(bookmark.id)}
                                            isSelectionMode={selectedBookmarkIds.size > 0}
                                            onToggleSelect={handleToggleSelect}
                                            viewMode={viewMode}
                                        />
                                    </div>
                                ))}
                                {!isSidePanel && viewMode === 'grid' && (
                                    <div className="h-full">
                                        <button 
                                            onClick={() => { setEditingBookmark(null); setIsEditModalOpen(true); }}
                                            className="w-full h-full min-h-[140px] flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-400 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group"
                                        >
                                            <Plus size={32} className="mb-2 group-hover:scale-110 transition-transform" />
                                            <span className="text-sm font-medium">添加收藏</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </SortableContext>
                    </div>
                </DndContext>
                        
                        {!isSidePanel && (
                            <div className="mt-8">
                                <BookmarkletInfo />
                            </div>
                        )}
                </>
            )}

            {activeTab === 'ai' && (
                <div className="bg-white dark:bg-slate-800 rounded-xl p-8 border border-slate-200 dark:border-slate-700">
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-2">AI 助手列表管理</h2>
                        <p className="text-sm text-slate-500">这里管理首页显示的 AI 助手快捷方式。支持拖拽排序。</p>
                    </div>
                    <AIPlatforms variant="card" />
                </div>
            )}
        </>
    )}
        
        {/* Floating Action Button for Side Panel */}
        {isSidePanel && (
             <button
                onClick={() => window.open(chrome.runtime.getURL('index.html'), '_blank')}
                className="fixed bottom-6 right-6 w-12 h-12 bg-blue-600 text-white rounded-full shadow-xl hover:bg-blue-700 transition-all z-50 flex items-center justify-center hover:scale-110 active:scale-95"
                title="打开完整管理面板"
            >
                <Maximize2 size={20} />
            </button>
        )}

        <BookmarkEditModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            bookmark={editingBookmark}
            tags={tags}
            onSave={handleSaveBookmark}
        />
        
        {!isSidePanel && selectedBookmarkIds.size > 0 && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-slate-200 dark:border-slate-700 shadow-2xl rounded-2xl px-6 py-3 animate-in fade-in slide-in-from-bottom-4">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 mr-2 border-r border-slate-200 dark:border-slate-700 pr-4">
                    已选 {selectedBookmarkIds.size} 项
                </span>
                <button 
                    onClick={handleSelectGroup}
                    className="px-3 py-1.5 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-sm font-medium flex items-center gap-1 transition-colors"
                    title="选中当前分组下的所有书签"
                >
                    <CheckSquare size={16} /> 全选分组
                </button>
                <button 
                    onClick={() => setIsBatchTagModalOpen(true)}
                    className="px-3 py-1.5 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                    <TagIcon size={16} /> 批量标签
                </button>
                <button 
                    onClick={handleBatchDelete}
                    className="px-3 py-1.5 bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                    <Trash2 size={16} /> 批量删除
                </button>
                <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1"></div>
                <button 
                    onClick={() => setSelectedBookmarkIds(new Set())}
                    className="p-1.5 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                    title="取消选择 (Esc)"
                >
                    <X size={18} />
                </button>
            </div>
        )}

        {isBatchTagModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                <div className="bg-white dark:bg-slate-900 rounded-xl p-6 w-full max-w-md shadow-2xl">
                    <h3 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">批量添加标签</h3>
                     <BatchTagSelector 
                        tags={tags} 
                        onConfirm={handleBatchAddTags} 
                        onCancel={() => setIsBatchTagModalOpen(false)} 
                     />
                </div>
            </div>
        )}

      </div>
    </div>
  );
};

export default Bookmarks;